import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { ApiError } from '../../api/problem'
import { confirmEmail } from '../../auth/session'
import { Banner, Button } from '../../design/components'
import { AuthLayout } from './AuthLayout'
import styles from './AuthForm.module.css'

type State =
  { kind: 'working' } | { kind: 'done'; message: string } | { kind: 'failed'; message: string }

/**
 * The landing page for the emailed confirmation link.
 *
 * It takes `userId` and `code` from its own query string and calls the API's
 * confirm endpoint, so the user sees a themed page rather than the raw JSON the
 * API returns when its link is followed directly.
 */
export function ConfirmEmailPage() {
  const [params] = useSearchParams()
  const [state, setState] = useState<State>({ kind: 'working' })

  const userId = params.get('userId')
  const code = params.get('code')

  // React 19 runs effects twice in StrictMode. The confirm token is single-use,
  // so a second call answers "confirmation failed" and would show the user an
  // error for something that had just succeeded.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    if (userId === null || code === null) {
      setState({
        kind: 'failed',
        message: 'This link is missing its confirmation details. Use the link from your email.',
      })
      return
    }

    confirmEmail(userId, code)
      .then((message) => setState({ kind: 'done', message }))
      .catch((cause: unknown) =>
        setState({
          kind: 'failed',
          message:
            cause instanceof ApiError && cause.kind === 'network'
              ? 'Could not reach the server. Check that the API is running.'
              : 'That confirmation link is invalid or has already been used.',
        }),
      )
  }, [userId, code])

  return (
    <AuthLayout title="Confirm your email">
      {state.kind === 'working' && <p className={styles.note}>Confirming…</p>}

      {state.kind === 'done' && (
        <>
          <Banner tone="ember">{state.message}</Banner>
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <Link to="/login">
              <Button fullWidth>Sign in</Button>
            </Link>
          </div>
        </>
      )}

      {state.kind === 'failed' && (
        <>
          <Banner tone="error" urgent>
            {state.message}
          </Banner>
          <p className={`${styles.note} ${styles.center}`} style={{ marginTop: 'var(--sp-4)' }}>
            <Link to="/login">Back to sign in</Link>
          </p>
        </>
      )}
    </AuthLayout>
  )
}
